import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table, Icon, Popup } from "semantic-ui-react";
import { Pie, Column } from "@ant-design/plots";
import jsPDF from "jspdf";
import { LegalRequirement, User, Requirement } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite } from "../../../../contexts";
import { useCompany } from "../../../../contexts/CompanyContext";
import { LoadingSpinner, ExportPdfButton, KPICard, YearBar, ChartBox, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf, hexToRgb, SEMANTIC_HEX } from "../../../Shared/pdfCharts";
import { CHART_PALETTE as CHART_COLORS } from "../../../../utils/chartTheme";
import "./LegalRequirementsDashboard.scss";

// Colores fijos por estado — pill en vez del Label semántico de antes.
// Texto blanco solo para "pending" (único color oscuro de los 4).
const STATUS_PILL_COLORS = {
  pending:       { bg: "#001936", color: "#fff" },
  non_compliant: { bg: "#8CCAFF", color: "#1a1c40" },
  compliant:     { bg: "#A3B76B", color: "#1a1c40" },
  partial:       { bg: "#C4D693", color: "#1a1c40" },
};

// ── Estilos heredados de Inicio (GeneralDashboard) — mismos colores/
// tipografías, para que ambos dashboards se vean consistentes.
const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
// Gama para las donas de acá (área/frecuencia, sin mapeo semántico fijo
// posible ya que las categorías son dinámicas) — mismos verdes que la
// barra mensual de al lado (antes mezclaba un azul/negro que desentonaba
// con el resto de los gráficos, todos en tonos verdes, del dashboard).
const DONUT_PALETTE = ["#95A472", "#4A5530", "#C4D693", "#7A8A4F", "#B3C08F", "#5E6B3C"];
const MONTH_BAR_STYLE = { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 };
const MONTH_BAR_SCALE_X = { padding: 0.08, paddingInner: 0.08, paddingOuter: 0.08 };
const MONTH_BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const MONTH_BAR_X_AXIS = { labelFill: "#001936", labelOpacity: 1 };
const MONTH_BAR_INTERACTION = { tooltip: { crosshairs: false }, elementHighlightByX: false };

// Mismos 3 colores (Alto/Medio/Bajo) que la leyenda "Referencias" de
// Comparativa en Inicio.
const LEGEND_SWATCH_COLORS = {
  high: { blue: "#8CCAFF", green: "#B0C676" },
  mid:  { blue: "#BFD8ED", green: "#C8E087" },
  low:  { blue: "#DBEFFF", green: "#DDFCAD" },
};
const LEGEND_STEPS = [
  { key: "high", labelKey: "high" },
  { key: "mid",  labelKey: "medium" },
  { key: "low",  labelKey: "low" },
];

// Mismo cálculo que compPillStyle en Inicio — interpola entre los 3
// colores de la leyenda según el valor (0-100).
function lrHexToRgb(hex) {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}
const LEGEND_SCALE_RGB = {
  blue:  { low: lrHexToRgb(LEGEND_SWATCH_COLORS.low.blue),  mid: lrHexToRgb(LEGEND_SWATCH_COLORS.mid.blue),  high: lrHexToRgb(LEGEND_SWATCH_COLORS.high.blue) },
  green: { low: lrHexToRgb(LEGEND_SWATCH_COLORS.low.green), mid: lrHexToRgb(LEGEND_SWATCH_COLORS.mid.green), high: lrHexToRgb(LEGEND_SWATCH_COLORS.high.green) },
};
function legendPillStyle(val, max = 100, tone = "green") {
  const ratio = max > 0 ? Math.min(1, Math.max(0, val / max)) : 0;
  const { low, mid, high } = LEGEND_SCALE_RGB[tone];
  const [r, g, b] = ratio <= 0.5
    ? low.map((c, i) => Math.round(c + (mid[i] - c) * (ratio / 0.5)))
    : mid.map((c, i) => Math.round(c + (high[i] - c) * ((ratio - 0.5) / 0.5)));
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return { backgroundColor: `rgb(${r},${g},${b})`, color: brightness < 150 ? "#fff" : "#1a1c40" };
}

const EVIDENCE_LABELS = {
  licencia:           "Licencia",
  permiso:            "Permiso",
  informe:            "Informe / Análisis",
  registros_firmados: "Registros firmados",
  ddjj:               "DDJJ",
  contrato_servicio:  "Contrato de servicio",
  certificado:        "Certificado profesional",
  constancia_dc3:     "Constancia DC-3",
  acta_inspeccion:    "Acta de inspección",
  fotografia:         "Fotografía",
  otro:               "Otro",
};

function formatDate(date) {
  if (!date) return "-";
  const str = typeof date === "string" ? date : date.toISOString();
  const [y, m, d] = str.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
}

function getAlertLevel(item) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const warn30 = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const nextOccDate = (item.occurrences || [])
    .filter((o) => !o.completed && o.date)
    .map((o) => new Date(o.date))
    .sort((a, b) => a - b)[0];
  const date = nextOccDate ?? (item.expiration ? new Date(item.expiration) : null);
  if (!date || isNaN(date)) return null;
  if (date < today) return "expired";
  if (date <= warn30) return "warning";
  return null;
}

function ReadOnlyTable({ items, t, showSite }) {
  const [sortCol, setSortCol] = React.useState(null);
  const [sortDir, setSortDir] = React.useState("ascending");

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => d === "ascending" ? "descending" : "ascending");
    } else {
      setSortCol(col);
      setSortDir("ascending");
    }
  };

  const sorted = React.useMemo(() => {
    if (!sortCol) return items;
    return [...items].sort((a, b) => {
      let va = "", vb = "";
      if (sortCol === "area")   { va = a.area?.name   || ""; vb = b.area?.name   || ""; }
      if (sortCol === "status") { va = a.status        || ""; vb = b.status        || ""; }
      const cmp = va.localeCompare(vb);
      return sortDir === "ascending" ? cmp : -cmp;
    });
  }, [items, sortCol, sortDir]);

  const thProps = (col) => ({
    onClick: () => handleSort(col),
    style: { cursor: "pointer", userSelect: "none" },
  });

  if (!items.length) {
    return <p style={{ textAlign: "center", color: "#999", padding: "1.5rem 0" }}>{t("no_results")}</p>;
  }
  return (
    <div className="lr-dashboard__ro-table-wrapper">
      <Table className="lr-dashboard__ro-table unstackable rtable-cards">
        <Table.Header>
          <Table.Row>
            {showSite && <Table.HeaderCell>{t("site")}</Table.HeaderCell>}
            <Table.HeaderCell {...thProps("area")}>{t("area")}</Table.HeaderCell>
            <Table.HeaderCell>Norma</Table.HeaderCell>
            <Table.HeaderCell>Requisito</Table.HeaderCell>
            <Table.HeaderCell>{t("authority")}</Table.HeaderCell>
            <Table.HeaderCell>{t("evidence")}</Table.HeaderCell>
            <Table.HeaderCell>{t("responsible")}</Table.HeaderCell>
            <Table.HeaderCell>{t("frequency")}</Table.HeaderCell>
            <Table.HeaderCell>{t("expiration")}</Table.HeaderCell>
            <Table.HeaderCell {...thProps("status")}>{t("estado")}</Table.HeaderCell>
            <Table.HeaderCell>{t("advance")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sorted.map((item) => (
            <Table.Row key={item._id}>
              {showSite && <Table.Cell data-label={t("site")}>{item.site?.name || "-"}</Table.Cell>}
              <Table.Cell data-label={t("area")}>{item.area?.name        || "-"}</Table.Cell>
              <Table.Cell data-label="Norma">{item.source?.name      || "-"}</Table.Cell>
              <Table.Cell data-label="Requisito">{item.requirement?.name || "-"}</Table.Cell>
              <Table.Cell data-label={t("authority")}>{item.authority?.name   || "-"}</Table.Cell>
              <Table.Cell data-label={t("evidence")}>
                {item.evidenceTypes?.length
                  ? item.evidenceTypes.map((e) => EVIDENCE_LABELS[e] || e).join(", ")
                  : "-"}
              </Table.Cell>
              <Table.Cell data-label={t("responsible")}>{item.responsible || "-"}</Table.Cell>
              <Table.Cell data-label={t("frequency")}>{item.frequency?.name   || "-"}</Table.Cell>
              <Table.Cell data-label={t("expiration")}>
                {(() => {
                  const alert = getAlertLevel(item);
                  const color = alert === "expired" ? "#e53935" : alert === "warning" ? "#f57c00" : "#555";
                  const icon  = alert === "expired" ? "exclamation circle" : alert === "warning" ? "exclamation triangle" : null;
                  const tip   = alert === "expired" ? "Vencido" : alert === "warning" ? "Vence en menos de 30 días" : null;
                  const dateStr = item.occurrences?.length > 1
                    ? `${formatDate(item.occurrences[0].date)} → ${formatDate(item.occurrences[item.occurrences.length - 1].date)}`
                    : formatDate(item.occurrences?.[0]?.date || item.expiration);
                  const inner = (
                    <span style={{ fontSize: "0.82em", color, fontWeight: alert ? 600 : "inherit", whiteSpace: "nowrap" }}>
                      {dateStr}{icon && <Icon name={icon} style={{ marginLeft: 4 }} />}
                    </span>
                  );
                  return tip ? <Popup size="mini" content={tip} trigger={inner} /> : inner;
                })()}
              </Table.Cell>
              <Table.Cell data-label={t("estado")}>
                {item.status
                  ? (() => {
                      const pill = STATUS_PILL_COLORS[item.status] || { bg: "#E4E4E4", color: "#1a1c40" };
                      return (
                        <span className="lr-dashboard__row-pill" style={{ backgroundColor: pill.bg, color: pill.color }}>
                          {t(`status_${item.status}`) || item.status}
                        </span>
                      );
                    })()
                  : "-"}
              </Table.Cell>
              <Table.Cell data-label={t("advance")}>
                <span className="lr-dashboard__row-pill" style={legendPillStyle(item.progress || 0, 100, "green")}>
                  {item.progress || 0}%
                </span>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

const lrController          = new LegalRequirement();
const userController        = new User();
const requirementController = new Requirement();

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { key: y, value: y, text: String(y) };
});

const MONTH_KEYS = ["month_jan", "month_feb", "month_mar", "month_apr", "month_may", "month_jun", "month_jul", "month_aug", "month_sep", "month_oct", "month_nov", "month_dec"];

function EmptyMsg({ text = "Sin datos" }) {
  return <div className="lr-dashboard__empty">{text}</div>;
}

const FilterDropdown = ({ options, value, onChange, placeholder, clearable = false }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // El menú abre siempre hacia abajo — si el filtro queda cerca del borde
  // inferior de la pantalla (ej. justo arriba de la tabla), no entra y se
  // corta contra el viewport. Si no hay lugar debajo pero sí arriba, abre
  // para arriba en su lugar.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuMaxHeight = 340;
      setOpenUp(window.innerHeight - rect.bottom < menuMaxHeight && rect.top > menuMaxHeight);
    }
    setOpen((v) => !v);
  };

  const selected = options.find((o) => String(o.value) === String(value));
  const hasValue = clearable && Boolean(value);

  return (
    <div className="lrd-fd" ref={ref}>
      <button type="button" className="lrd-fd-toggle" onClick={toggleOpen}>
        <span className={selected ? "" : "lrd-fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        {hasValue && (
          <span
            role="button"
            aria-label="clear"
            className="lrd-fd-clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"/>
            </svg>
          </span>
        )}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`lrd-fd-arrow${open ? " lrd-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className={`lrd-fd-menu${openUp ? " lrd-fd-menu--up" : ""}`}>
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`lrd-fd-item${String(o.value) === String(value) ? " lrd-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export function LegalRequirementsDashboard({ companyName, siteName } = {}) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const { site } = useSite();
  const { company } = useCompany();
  const t = (key) => translations[key] || key;

  const [year,    setYear]    = useState(CURRENT_YEAR);
  const kpiRef    = useRef();
  const tableRef  = useRef();
  const [items,   setItems]   = useState(null);
  const [reload,  setReload]  = useState(false);
  const onReload = () => setReload((p) => !p);

  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  // Filters
  const [filterSearch,      setFilterSearch]      = useState("");
  const [filterResponsible, setFilterResponsible] = useState("");
  const [filterRequirement, setFilterRequirement] = useState("");
  const [filterStatus,      setFilterStatus]      = useState("");

  // Catalog options for filters
  const [catUsers,        setCatUsers]        = useState([]);
  const [catRequirements, setCatRequirements] = useState([]);

  // Load requirements catalog once
  useEffect(() => {
    (async () => {
      try {
        const reqs = await requirementController.getRequirements(accessToken);
        setCatRequirements(reqs.map((x) => ({ key: x._id, value: x._id, text: x.name })));
      } catch {}
    })();
  }, []);

  // Load site users whenever site changes
  useEffect(() => {
    if (!site) return;
    (async () => {
      try {
        const users = await userController.getUsersBySite(accessToken, site, true);
        const opts = users.map((u) => {
          const fullName = [u.name, u.lastname].filter(Boolean).join(" ");
          return { key: u._id, value: fullName, text: fullName };
        });
        setCatUsers(opts);
      } catch {}
    })();
  }, [site]);

  // Load requirements data
  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const filters = {};
        if (site) filters.site = site;
        if (company) filters.company = company;
        const res = await lrController.getLegalRequirements(accessToken, filters);
        setItems(res);
      } catch {
        setItems([]);
      }
    })();
  }, [site, company, reload]);

  // Items filtered by year + dashboard filters
  const filteredItems = useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      // Year filter
      const inYear = (() => {
        if (item.occurrences?.length)
          return item.occurrences.some((o) => new Date(o.date).getFullYear() === year);
        if (item.expiration) return new Date(item.expiration).getFullYear() === year;
        return new Date(item.createdAt).getFullYear() === year;
      })();
      if (!inYear) return false;

      // Search filter
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const matches =
          item.requirement?.name?.toLowerCase().includes(q) ||
          item.area?.name?.toLowerCase().includes(q) ||
          item.responsible?.toLowerCase().includes(q) ||
          item.status?.toLowerCase().includes(q) ||
          item.observations?.toLowerCase().includes(q);
        if (!matches) return false;
      }

      // Responsible filter
      if (filterResponsible && !(item.responsible || "").toLowerCase().includes(filterResponsible.toLowerCase())) return false;

      // Requirement filter
      const reqId = item.requirement?._id || item.requirement || "";
      if (filterRequirement && reqId !== filterRequirement) return false;

      // Status filter
      if (filterStatus && item.status !== filterStatus) return false;

      return true;
    });
  }, [items, year, filterSearch, filterResponsible, filterRequirement, filterStatus]);

  // KPIs
  const kpis = useMemo(() => {
    if (!filteredItems.length) return { cumplimiento: 0, enTramite: 0, vencidos: 0, proximos60: 0 };

    const cumplimiento = Math.round(
      filteredItems.reduce((sum, i) => sum + (i.progress || 0), 0) / filteredItems.length
    );
    const enTramite = filteredItems.filter((i) => i.status === "en_revision").length;
    const vencidos  = filteredItems.filter((i) => i.status === "vencido").length;

    const now = new Date();
    const d60 = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
    const proximos60 = filteredItems.filter((item) =>
      (item.occurrences || []).some((o) => {
        const d = new Date(o.date);
        return !o.completed && d >= now && d <= d60;
      })
    ).length;

    return { cumplimiento, enTramite, vencidos, proximos60 };
  }, [filteredItems]);

  // Chart 1: Requisitos por área (Donut)
  const areaData = useMemo(() => {
    const counts = {};
    filteredItems.forEach((item) => {
      const name = item.area?.name?.trim();
      if (!name) return;
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filteredItems]);

  // Chart 2: Vencimientos por mes (Column)
  const monthData = useMemo(() => {
    const counts = Array(12).fill(0);
    filteredItems.forEach((item) => {
      const occs = item.occurrences || [];
      if (occs.length) {
        occs.forEach((o) => {
          const d = new Date(o.date);
          if (d.getFullYear() === year && !o.completed) counts[d.getMonth()]++;
        });
      } else if (item.expiration) {
        const d = new Date(item.expiration);
        if (d.getFullYear() === year) counts[d.getMonth()]++;
      }
    });
    return MONTH_KEYS.map((key, i) => ({ mes: t(key), value: counts[i] }));
  }, [filteredItems, year]);

  // Chart 3: Requisitos por frecuencia (Donut)
  const freqData = useMemo(() => {
    const counts = {};
    filteredItems.forEach((item) => {
      const name = item.frequency?.name || t("no_frequency");
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filteredItems]);

  const pieConfig = (data) => {
    const colors = data.map((_, i) => DONUT_PALETTE[i % DONUT_PALETTE.length]);
    const total = data.reduce((sum, d) => sum + d.value, 0);
    return {
      data,
      angleField: "value",
      colorField: "type",
      innerRadius: 0.6,
      scale: { color: { range: colors } },
      label: false,
      legend: false,
      tooltip: {
        title: "type",
        items: [
          {
            field: "value",
            name: t("count"),
            valueFormatter: (v) => `${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : "0.0"}%)`,
          },
        ],
      },
      height: 250,
    };
  };

  const colConfig = {
    data: monthData,
    xField: "mes",
    yField: "value",
    colorField: "mes",
    scale: { color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X },
    legend: false,
    height: 250,
    style: MONTH_BAR_STYLE,
    axis: { x: MONTH_BAR_X_AXIS, y: MONTH_BAR_Y_AXIS },
    tooltip: { items: [{ channel: "y", name: t("expirations_by_month") }] },
    interaction: MONTH_BAR_INTERACTION,
  };

  const hasMonthData = monthData.some((d) => d.value > 0);

  const STATUS_OPTIONS = [
    { key: "vigente",     value: "vigente",     text: t("status_vigente") },
    { key: "vencido",     value: "vencido",     text: t("status_vencido") },
    { key: "pendiente",   value: "pendiente",   text: t("status_pendiente") },
    { key: "en_revision", value: "en_revision", text: t("status_en_revision") },
  ];

  const responsibleOptions = catUsers;

  const requirementOptions = catRequirements;

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const areaColorMap = Object.fromEntries(areaData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));
      const freqColorMap = Object.fromEntries(freqData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));

      const [areaDonut, monthColumn, freqDonut] = await Promise.all([
        renderDonutForPdf(areaData, areaColorMap, { showPercentLabels: true }),
        renderColumnForPdf(monthData, { xField: "mes", fillColor: CHART_COLORS[5] }),
        renderDonutForPdf(freqData, freqColorMap, { showPercentLabels: true }),
      ]);
      [areaDonut, monthColumn, freqDonut].forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiCanvas = await captureElementAtWidth(kpiRef.current, 1400, {}, prepareKpiCardsForPdf);

      const tableColumns = [
        // Con "Total Grupo" (sin sitio puntual elegido) los datos vienen de
        // varios sitios mezclados — sin esta columna no se puede saber a
        // cuál pertenece cada fila (igual que en la tabla en pantalla).
        ...(!site ? [{ key: "site", header: t("site"), flex: 1, value: (r) => r.site?.name || "-" }] : []),
        { key: "area", header: t("area"), flex: 1, value: (r) => r.area?.name || "-" },
        { key: "norma", header: "Norma", flex: 1, value: (r) => r.source?.name || "-" },
        { key: "requisito", header: "Requisito", flex: 1.3, value: (r) => r.requirement?.name || "-" },
        { key: "authority", header: t("authority"), flex: 1, value: (r) => r.authority?.name || "-" },
        {
          key: "evidence", header: t("evidence"), flex: 1.3,
          value: (r) => (r.evidenceTypes?.length ? r.evidenceTypes.map((e) => EVIDENCE_LABELS[e] || e).join(", ") : "-"),
        },
        { key: "responsible", header: t("responsible"), flex: 1, value: (r) => r.responsible || "-" },
        { key: "frequency", header: t("frequency"), flex: 0.9, value: (r) => r.frequency?.name || "-" },
        {
          key: "expiration", header: t("expiration"), flex: 1.2,
          render: (doc, { x, y, width, height, row }) => {
            const alert = getAlertLevel(row);
            const color = alert === "expired" ? "#e53935" : alert === "warning" ? "#f57c00" : "#555555";
            // jsPDF con las fuentes estándar (Helvetica) sólo soporta WinAnsi/CP1252: la
            // flecha "→" no está en esa tabla y corrompe toda la celda al dibujarla acá
            // (a diferencia del HTML en pantalla, que sí la renderiza bien).
            const dateStr = row.occurrences?.length > 1
              ? `${formatDate(row.occurrences[0].date)} - ${formatDate(row.occurrences[row.occurrences.length - 1].date)}`
              : formatDate(row.occurrences?.[0]?.date || row.expiration);
            doc.setFontSize(8);
            doc.setFont(undefined, alert ? "bold" : "normal");
            doc.setTextColor(...hexToRgb(color));
            const text = doc.splitTextToSize(dateStr, width - 10)[0] || "-";
            doc.text(text, x + 6, y + height / 2 + 3);
          },
        },
        {
          key: "status", header: t("estado"), flex: 0.9, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            if (!row.status) return;
            const color = STATUS_PILL_COLORS[row.status]?.bg || SEMANTIC_HEX.grey;
            doc.setFontSize(8);
            doc.setFont(undefined, "bold");
            doc.setTextColor(...hexToRgb(color));
            doc.text(t(`status_${row.status}`) || row.status, x + width / 2, y + height / 2 + 3, { align: "center" });
          },
        },
        {
          key: "advance", header: t("advance"), flex: 1.1, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            doc.setFontSize(8);
            doc.setFont(undefined, "bold");
            doc.setTextColor(0, 25, 54);
            doc.text(`${row.progress || 0}%`, x + width / 2, y + height / 2 + 3, { align: "center" });
          },
        },
      ];

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("requirements_title")} — ${year}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas,
        chartRows: [
          {
            height: 210,
            cards: [
              { title: t("requirements_by_area"), ...areaDonut, legendPosition: "right" },
              { title: t("expirations_by_month"), ...monthColumn },
              { title: t("requirements_by_frequency"), ...freqDonut, legendPosition: "right" },
            ],
          },
        ],
        table: { columns: tableColumns, rows: filteredItems, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
        tableTitle: `${t("requirements_title")} — ${year}`,
        filename: `requisitos_legales_${year}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  return (
    <div className="lr-dashboard">
      {/* KPI wrapper */}
      {/* ── Filtros — afuera de la card gris, arriba de todo (igual que Inicio) */}
      <div className="lr-dashboard__header">
        <YearBar year={year} options={YEAR_OPTIONS} onChange={setYear} />
        <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
      </div>

      <div className="resumen-wrapper lr-dashboard__kpi-wrapper">
        {items === null ? (
          <LoadingSpinner />
        ) : (
          <div className="lr-dashboard__kpis" ref={kpiRef}>
            <KPICard
              label={t("compliance_percent")}
              value={`${kpis.cumplimiento}%`}
              rawValue={kpis.cumplimiento}
              showProgress
              progressColor="blue"
            />
            <KPICard label={t("en_tramite")}     value={kpis.enTramite} />
            <KPICard label={t("status_vencido")} value={kpis.vencidos} />
            <KPICard
              label={t("next_60_days")}
              value={kpis.proximos60}
              subtitle={t("pending_label")}
            />
          </div>
        )}
      </div>

      {items !== null && (
        <>
          {/* Charts — mismo patrón que Inicio: rectángulo gris por chart
              (.lr-dashboard__chart-slot) + <ChartBox> (título/contenido
              blancos separados) + <ChartLegend> al lado de la dona. Sin
              datos: anillo gris vacío (no texto), como en la referencia. */}
          <div className="lr-dashboard__charts">
            <div className="lr-dashboard__chart-slot">
              <ChartBox title={t("requirements_by_area")}>
                {areaData.length > 0 ? (
                  <div className="lr-dashboard__pie-row">
                    <div className="chartWrapper lr-dashboard__pie-wrapper">
                      <Pie {...pieConfig(areaData)} />
                    </div>
                    <ChartLegend items={areaData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
                  </div>
                ) : (
                  <div className="chartWrapper lr-dashboard__pie-wrapper"><div className="lr-dashboard__empty-ring" /></div>
                )}
              </ChartBox>
            </div>
            <div className="lr-dashboard__chart-slot">
              <ChartBox title={t("expirations_by_month")}>
                <div className="chartWrapper">
                  {hasMonthData ? <Column {...colConfig} /> : <EmptyMsg />}
                </div>
              </ChartBox>
            </div>
            <div className="lr-dashboard__chart-slot">
              <ChartBox title={t("requirements_by_frequency")}>
                {freqData.length > 0 ? (
                  <div className="lr-dashboard__pie-row">
                    <div className="chartWrapper lr-dashboard__pie-wrapper">
                      <Pie {...pieConfig(freqData)} />
                    </div>
                    <ChartLegend items={freqData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
                  </div>
                ) : (
                  <div className="chartWrapper lr-dashboard__pie-wrapper"><div className="lr-dashboard__empty-ring" /></div>
                )}
              </ChartBox>
            </div>
          </div>
        </>
      )}

      {/* Filtros + tabla — mismo envoltorio gris que el resto del
          dashboard (.gen-dash__comparativa en Inicio): un solo rectángulo
          gris con los filtros arriba (pills blancos) y la tabla abajo. */}
      {items !== null && (
        <div className="lr-dashboard__data-wrap">
          <div className="lr-dashboard__filters">
            <div className="lr-dashboard__search-wrapper">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                className="lr-dashboard__search-input"
                placeholder={`${t("search")}...`}
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
            </div>
            <div className="lr-dashboard__filter-select lr-dashboard__filter-select--text">
              <input
                placeholder={t("responsible")}
                value={filterResponsible}
                onChange={(e) => setFilterResponsible(e.target.value)}
              />
            </div>
            <div className="lr-dashboard__filter-select">
              <FilterDropdown
                options={requirementOptions}
                value={filterRequirement}
                onChange={setFilterRequirement}
                placeholder={t("requirement")}
                clearable
              />
            </div>
            <div className="lr-dashboard__filter-select">
              <FilterDropdown
                options={STATUS_OPTIONS}
                value={filterStatus}
                onChange={setFilterStatus}
                placeholder={t("estado")}
                clearable
              />
            </div>
          </div>

          <div className="lr-dashboard__table-section" ref={tableRef}>
            <ReadOnlyTable items={filteredItems} t={t} showSite={!site} />
          </div>

          {/* Referencias — mismo patrón que Comparativa en Inicio. */}
          <div className="lr-dashboard__legend">
            <span className="lr-dashboard__legend-title">{t("references")}</span>
            <div className="lr-dashboard__legend-row">
              {LEGEND_STEPS.map((step) => (
                <div key={step.key} className="lr-dashboard__legend-group">
                  <span className="lr-dashboard__legend-label">{t(step.labelKey)}</span>
                  <span className="lr-dashboard__legend-swatches">
                    {["blue", "green"].map((tone) => (
                      <span
                        key={tone}
                        className="lr-dashboard__legend-swatch"
                        style={{ backgroundColor: LEGEND_SWATCH_COLORS[step.key][tone] }}
                      />
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
